import { ChangeEvent, FormEvent, useEffect, useState } from 'react';
import { useBeforeunload } from 'react-beforeunload';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { selectUser } from '../../../features/auth/authSlice';
import {
  useFinishQuizMutation,
  useSendAnswersMutation,
} from '../../../features/exam/certificationApiSlice';
import { selectQuestion } from '../../../features/exam/examSlice';
import { sleep } from '../../../hooks/sleep';
import { useCallbackPrompt } from '../../../hooks/useCallbackPrompt';
import { Answer, QuizLog } from '../../../types/certifications/Certification';
import { Button } from '../../Button';
import { DialogBox } from '../../DialogBox';

type PropsFormQuestion = {
  quizLog: QuizLog;
  finalize: boolean;
  logId: string;
};

const answers: string[] = [];

export function FormQuestion({ quizLog, finalize, logId }: PropsFormQuestion) {
  const navigate = useNavigate();
  const user = useSelector(selectUser);
  const questions = useSelector(selectQuestion);
  const [showDialog, setShowDialog] = useState<boolean>(true);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [showPrompt, confirmNavigation, cancelNavigation] =
    useCallbackPrompt(showDialog);

  const [sendAnswers] = useSendAnswersMutation();
  const [finishQuiz] = useFinishQuizMutation();

  function handleQuestionToSend() {
    const payloadQuestions: Answer[] = [];

    for (let i = 0; i < answers.length; i++) {
      const q = answers[i];
      const questionId = questions[i]._id;

      payloadQuestions.push({
        player: user!._id,
        question: questionId,
        answer: [q],
        quiz: quizLog.quiz,
        quiz_log: quizLog._id,
      });
    }

    return payloadQuestions;
  }

  function finalizeExam(e?: FormEvent<HTMLFormElement>) {
    if (e) e.preventDefault();

    setIsSubmitting(true);

    if (questions?.[0]) {
      setShowDialog(false);
      try {
        const payloadQuestions = handleQuestionToSend();

        sendAnswers(payloadQuestions)
          .then(() => {
            sleep(5000).then(() => {
              finishQuiz(quizLog._id)
                .then(() => {
                  setIsSubmitting(false);
                  navigate(`/result/${logId}`);
                })
                .catch(erroFinishQuiz => {
                  setIsSubmitting(false);
                  console.error(erroFinishQuiz);
                });
            });
          })
          .catch(erroSendAnswers => {
            setIsSubmitting(false);
            console.error(erroSendAnswers);
          });
      } catch (error) {
        setIsSubmitting(false);
        console.error(error);
      }
    }
  }

  useEffect(() => {
    if (finalize) finalizeExam();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finalize]);

  useEffect(() => {
    if (isSubmitting) {
      window.scrollTo(0, 0);
    }
  }, [isSubmitting]);

  const handleChangeQuestion = (
    e: ChangeEvent<HTMLInputElement>,
    index: number,
  ) => {
    const { value } = e.target;
    answers[index] = value;

    handleQuestionToSend();
  };

  useBeforeunload(event => {
    event.preventDefault();
  });

  if (isSubmitting)
    return (
      <div className="flex text-2xl absolute top-0 left-0 w-full h-screen bg-black/70 text-white items-center justify-center">
        <svg
          role="status"
          className="inline-flex justify-center self-center mr-3 w-20 h-20 text-white animate-spin"
          viewBox="0 0 100 101"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
            fill="#E5E7EB"
          />
          <path
            d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
            fill="currentColor"
          />
        </svg>
        <p>
          Enviando exame… <br />
          Por favor, aguarde enquanto seu exame está sendo enviado para
          avaliação.
          <br /> Isso pode levar alguns instantes.
        </p>
      </div>
    );

  return (
    <>
      <DialogBox
        showDialog={showPrompt}
        // @ts-ignore
        confirmNavigation={() => confirmNavigation(finalizeExam)}
        // @ts-ignore
        cancelNavigation={cancelNavigation}
      />

      <form className="mt-10 space-y-5" onSubmit={e => finalizeExam(e)}>
        {questions?.map((question, index) => (
          <div
            className="bg-slate-50 p-5 border border-solid border-slate-200 rounded-md space-y-1"
            key={question._id}
          >
            <p>
              {`${question.questionNumbering}. `}
              {question.question}
            </p>
            {question.choices.map((choice, indexChoice) => (
              <div className="flex items-center" key={choice.label}>
                <input
                  id={choice.answer + index + indexChoice}
                  type="radio"
                  value={choice.answer}
                  name={question.question}
                  onChange={el => handleChangeQuestion(el, index)}
                  className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 focus:ring-blue-500 "
                />
                <label
                  htmlFor={choice.answer + index + indexChoice}
                  className="ml-2 text-sm font-medium text-black "
                >
                  {`${choice.choiceNumbering}. `}
                  {choice.label}
                </label>
              </div>
            ))}
          </div>
        ))}

        <div className="w-fit ml-auto">
          <Button disabled={isSubmitting} type="submit">
            Enviar Prova
          </Button>
        </div>
      </form>
    </>
  );
}
